import {
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "./firebase";
import {
  normalizeSubscriptionStatus,
  resolveEffectiveSubscriptionAccess,
  type ResolvedAccessStatus,
  type NormalizedSubscriptionTier,
} from "./subscription-access";

export type EffectiveSubscriptionTier = NormalizedSubscriptionTier;
export type EffectiveSubscriptionSource = "subscriptions";

function toMillis(value: unknown): number {
  if (!value) return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  if (typeof value === "object") {
    const withToMillis = value as { toMillis?: () => number };
    if (typeof withToMillis.toMillis === "function") {
      return withToMillis.toMillis();
    }
    const withToDate = value as { toDate?: () => Date };
    if (typeof withToDate.toDate === "function") {
      const d = withToDate.toDate();
      return d instanceof Date ? d.getTime() : 0;
    }
  }
  return 0;
}

function getSubscriptionSortScore(subscription: Record<string, unknown>): number {
  const subscriptionAny = subscription as Record<string, any>;
  const status = normalizeSubscriptionStatus(
    subscriptionAny.status
  );
  const { hasPaidAccess } = resolveEffectiveSubscriptionAccess({
    tier:
      subscriptionAny.subscriptionTier ??
      subscriptionAny.planId,
    status,
    cancelAtPeriodEnd: subscriptionAny.cancelAtPeriodEnd,
  });
  const activeBonus = hasPaidAccess ? 10 ** 15 : 0;

  const latestTimestamp = Math.max(
    toMillis(subscription.updatedAt),
    toMillis(subscription.createdAt),
    toMillis(subscription.currentPeriodEnd),
    toMillis(subscription.currentPeriodStart)
  );

  return activeBonus + latestTimestamp;
}

function resolveSubscriptionDocState(subscription: any): {
  tier: EffectiveSubscriptionTier;
  status: string;
} {
  const status = normalizeSubscriptionStatus(subscription?.status);
  const { tier, status: resolvedAccessStatus } = resolveEffectiveSubscriptionAccess({
    tier:
      subscription?.subscriptionTier ??
      subscription?.planId ??
      "free",
    status,
    cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd ?? false,
  });
  return {
    tier,
    status: resolvedAccessStatus,
  };
}

export async function getUserSubscriptionRecord(
  userId: string
): Promise<Record<string, unknown> | null> {
  if (!userId) return null;

  // Canonical path going forward: subscriptions/{userId}.
  const canonicalSubscriptionDoc = await getDoc(doc(db, "subscriptions", userId));
  if (canonicalSubscriptionDoc.exists()) {
    return { id: canonicalSubscriptionDoc.id, ...canonicalSubscriptionDoc.data() };
  }

  return null;
}

export function resolveEffectiveSubscriptionState(
  userData: any,
  subscriptionRecord: Record<string, unknown> | null
): {
  tier: EffectiveSubscriptionTier;
  status: ResolvedAccessStatus;
  source: EffectiveSubscriptionSource;
  isConsistent: boolean;
} {
  void userData;
  if (!subscriptionRecord) {
    const tier: EffectiveSubscriptionTier = "free";
    const status: ResolvedAccessStatus = "inactive";
    return {
      tier,
      status,
      source: "subscriptions",
      isConsistent: true,
    };
  }

  const subscriptionState = resolveSubscriptionDocState(subscriptionRecord);
  const effectiveTier = subscriptionState.tier;
  const accessStatus = subscriptionState.status as ResolvedAccessStatus;
  return {
    tier: effectiveTier,
    status: accessStatus,
    source: "subscriptions",
    isConsistent: true,
  };
}

export async function ensureUserSubscriptionConsistency(
  userId: string,
  userData: any,
  resolved: {
    tier: EffectiveSubscriptionTier;
    status: ResolvedAccessStatus;
  }
) {
  void userId;
  void userData;
  void resolved;
  return false;
}

export async function resolveEffectiveSubscriptionForUser(
  userId: string,
  userData: any,
  options?: { syncUserDoc?: boolean }
) {
  const subscriptionRecord = await getUserSubscriptionRecord(userId);
  const resolved = resolveEffectiveSubscriptionState(userData, subscriptionRecord);
  if (options?.syncUserDoc) {
    await ensureUserSubscriptionConsistency(userId, userData, resolved);
  }
  return resolved;
}
